import { Component, OnInit, OnDestroy, inject, signal, input, output, ElementRef, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';
import { QueryDocumentSnapshot } from '@angular/fire/firestore';
import { ToastrService } from 'ngx-toastr';

import { NotificationService, AppNotification } from '../../../services/notification.service';
import { UsuarioService } from '../../../services/usuario.service';

@Component({
    selector: 'app-notification-dropdown',
    templateUrl: './notification-dropdown.component.html',
    styleUrls: ['./notification-dropdown.component.scss'],
    standalone: false
})
export class NotificationDropdownComponent implements OnInit, OnDestroy {
  private readonly notificationService = inject(NotificationService);
  private readonly usuarioService = inject(UsuarioService);
  private readonly router = inject(Router);
  private readonly toastr = inject(ToastrService);
  private destroy$ = new Subject<void>();
  private lastDoc: QueryDocumentSnapshot<any> | null = null;
  private readonly PAGE_SIZE = 10;
  private seenNotificationIds = new Set<string>();
  private isInitialInboxLoad = true;

  readonly sidebar = input(false);
  readonly compact = input(false);
  readonly opening = output<void>();
  @ViewChild('trigger') trigger?: ElementRef<HTMLButtonElement>;
  private readonly hostElement = inject(ElementRef<HTMLElement>);
  isOpen = signal(false);
  notifications = signal<AppNotification[]>([]);
  isLoading = signal(false);
  hasMore = signal(true);
  unreadCount = signal(0);

  ngOnInit(): void {
    const userId = this.usuarioService.uid;
    
    if (userId) {
      this.notificationService.getUnreadCount(userId)
        .pipe(takeUntil(this.destroy$))
        .subscribe(count => {
          this.unreadCount.set(count);
        });

      // ── Stream en tiempo real de Firestore para alertas instantáneas (Web & Tablet) ──
      this.notificationService.getInbox(userId)
        .pipe(takeUntil(this.destroy$))
        .subscribe(inbox => {
          if (this.isInitialInboxLoad) {
            inbox.forEach(n => {
              if (n.id) this.seenNotificationIds.add(n.id);
            });
            this.isInitialInboxLoad = false;
            return;
          }

          for (const n of inbox) {
            if (n.id && !this.seenNotificationIds.has(n.id) && !n.read) {
              this.seenNotificationIds.add(n.id);
              if (n.resourceType === 'tank_alert') {
                const title = n.title || '⛽ Critical Tank Alert';
                const body  = n.message || n.body || '';
                this.toastr.warning(body, title, {
                  timeOut: 10000,
                  closeButton: true,
                  progressBar: true,
                  enableHtml: false
                });
                this.refreshNotifications();
              }
            }
          }
        });
    }
    
    // Load initial notifications
    this.loadNotifications();

    this.notificationService.requestPermissionAndSave()
      .pipe(takeUntil(this.destroy$))
      .subscribe();

    this.notificationService.onForegroundMessage()
      .pipe(takeUntil(this.destroy$))
      .subscribe(payload => {
        this.refreshNotifications();
        // ── Alerta visual instantánea para tanques críticos (>80%) vía FCM ─────────
        if (payload?.data?.resourceType === 'tank_alert') {
          const notifId = payload?.data?.relatedResourceId || payload?.messageId;
          if (notifId && this.seenNotificationIds.has(notifId)) return;
          if (notifId) this.seenNotificationIds.add(notifId);

          const title = payload?.data?.title ?? payload?.notification?.title ?? '⛽ Critical Tank Alert';
          const body  = payload?.data?.body  ?? payload?.notification?.body  ?? '';
          this.toastr.warning(body, title, {
            timeOut: 10000,
            closeButton: true,
            progressBar: true,
            enableHtml: false
          });
        }
      });

    document.addEventListener('click', this.handleOutsideClick);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    document.removeEventListener('click', this.handleOutsideClick);
  }

  private loadNotifications(loadMore: boolean = false): void {
    const userId = this.usuarioService.uid;
    if (!userId || this.isLoading()) return;
    
    this.isLoading.set(true);
    
    this.notificationService.getInboxPaginated(
      userId, 
      this.PAGE_SIZE, 
      loadMore ? this.lastDoc ?? undefined : undefined
    )
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: ({ notifications, lastDoc, hasMore }) => {
          if (loadMore) {
            this.notifications.update(current => [...current, ...notifications]);
          } else {
            this.notifications.set(notifications);
          }
          
          this.lastDoc = lastDoc;
          this.hasMore.set(hasMore);
          this.isLoading.set(false);
        },
        error: () => {
          this.isLoading.set(false);
        }
      });
  }

  loadMore(): void {
    if (this.hasMore() && !this.isLoading()) {
      this.loadNotifications(true);
    }
  }

  refreshNotifications(): void {
    this.lastDoc = null;
    this.loadNotifications(false);
  }

  onScroll(event: Event): void {
    const element = event.target as HTMLElement;
    const threshold = 50;
    
    if (element.scrollHeight - element.scrollTop - element.clientHeight < threshold) {
      this.loadMore();
    }
  }

  private handleOutsideClick = (event: MouseEvent): void => {
    const target = event.target as HTMLElement;
    if (!this.hostElement.nativeElement.contains(target)) {
      this.isOpen.set(false);
    }
  };

  toggleDropdown(): void {
    if (!this.isOpen()) this.opening.emit();
    this.isOpen.update(open => !open);
  }

  closeDropdown(restoreFocus = false): void {
    this.isOpen.set(false);
    if (restoreFocus) this.trigger?.nativeElement.focus();
  }

  onNotificationClick(notification: AppNotification): void {
    this.closeDropdown();
    
    // Navigate to inbox with this notification selected
    this.router.navigate(['/uco/notifications'], {
      queryParams: { id: notification.id }
    });
  }

  markAllAsRead(): void {
    const unread = this.notifications().filter(n => !n.read);
    if (unread.length > 0) {
      this.notificationService.markAllAsRead(unread)
        .pipe(takeUntil(this.destroy$))
        .subscribe();
    }
  }

  trackByNotification(index: number, notification: AppNotification): string {
    return notification.id || index.toString();
  }

  formatTime(date: any): string {
    if (!date) return '';
    
    // Handle Firestore Timestamp
    const timestamp = date.toDate ? date.toDate() : new Date(date);
    const now = new Date();
    const diff = now.getTime() - timestamp.getTime();
    
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return 'Now';
    if (minutes < 60) return `${minutes} min ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;
    
    return timestamp.toLocaleDateString('en-US', {
      day: 'numeric',
      month: 'short'
    });
  }

  formatMessage(message: string): string {
    if (!message) return '';
    const preview = message.slice(0, 60);
    const cleanPreview = preview.replace(/<[^>]*>/g, ''); //eliminar etiquetas html
    return cleanPreview + (message.length > 60 ? '...' : '').replace(/\n/g, '<br>');
  }
}
